import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useModuleWorkspace from '@/data/hook/module/useModuleWorkspace';
import {
  CreateCourseModule,
  createCourseModule,
  UpdateCourseModule,
  updateCourseModule,
} from '@/data/services/courseModuleServices';
import { CourseModule } from '@/models/CourseModule';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { generateBannerPayload } from '@/utils/courses';
import { getErrorMessage } from '@/utils/getErrorMessage';

import ModuleForm, { ModuleFormFields } from './ModuleForm';

export type ModuleFormServiceSuccess = {
  item: CourseModule;
  action: 'update' | 'create';
};

type ModuleFormServiceProps = {
  onSuccess?: (success: ModuleFormServiceSuccess) => void;
};

export default function ModuleFormService({
  onSuccess,
}: ModuleFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.moduleForm.alerts',
  });
  const { module, courseLineId, cache } = useModuleWorkspace();

  const handleCreate = async (
    data: ModuleFormFields,
  ): Promise<CourseModule> => {
    const bannerPayload = await generateBannerPayload(data.bannerUrl);
    const payload: CreateCourseModule = {
      name: data.name,
      ...bannerPayload,
    };
    const createdModule = await createCourseModule(courseLineId, payload);
    alert.success(t('createSuccess'));
    return createdModule;
  };

  const handleUpdate = async (
    data: ModuleFormFields,
  ): Promise<CourseModule> => {
    if (!module) throw new Error('Module not found');
    const bannerPayload = await generateBannerPayload(data.bannerUrl);
    const payload: UpdateCourseModule = {
      name: data.name,
      ...bannerPayload,
    };
    const changedFields = buildChangedObject(module, payload);
    if (Object.keys(changedFields).length === 0) return module;
    const updatedModule = await updateCourseModule(
      courseLineId,
      module.id,
      changedFields,
    );
    alert.success(t('updateSuccess'));
    return updatedModule;
  };

  const handleSubmit = async (
    data: ModuleFormFields,
  ): Promise<ModuleFormServiceSuccess> => {
    if (!module) {
      const createdModule = await handleCreate(data);
      return { item: createdModule, action: 'create' };
    }
    const updatedModule = await handleUpdate(data);
    return { item: updatedModule, action: 'update' };
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async response => {
      cache?.merge(response.item);
      onSuccess?.(response);
    },
  });

  return (
    <ModuleForm module={module} onSubmit={onSubmit} isLoading={isPending} />
  );
}
